/**
 * Stage 5 client calls for one lead: the prompt log the Activity tab renders
 * next to the texts, and the manual "Nudge now".
 * Query key: ['lead', id, 'prompts'].
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, useApiClient } from '@/lib/api-client';
import { LEADS_KEY, leadKey } from '@/features/pipeline/api';
import type { Prompt } from '@/features/pipeline/types';
import { leadMessagesKey } from './messages-api';

export const leadPromptsKey = (id: string) => [...leadKey(id), 'prompts'] as const;

function unwrapList<T>(res: T[] | { items: T[] } | null | undefined): T[] {
  if (!res) return [];
  return Array.isArray(res) ? res : res.items ?? [];
}

export function useLeadPrompts(leadId: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: leadPromptsKey(leadId ?? ''),
    queryFn: async () =>
      unwrapList(await client.get<Prompt[] | { items: Prompt[] }>(`/api/leads/${leadId}/prompts`)),
    enabled: !!leadId,
    // A prompt settles when a manager texts back; poll gently while one is open.
    refetchInterval: (query) => (query.state.data?.some((p) => !p.answered_at) ? 30_000 : false),
  });
}

/** True when the worker refused because a nudge is already waiting for a reply (409 `prompt_open`). */
export function isPromptOpenError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 409 || err.code === 'prompt_open');
}

/** `POST /api/leads/:id/nudge` — texts the recipients now; refetches the lead, its prompts/texts and the board. */
export function useNudgeLead(leadId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.post<unknown>(`/api/leads/${leadId}/nudge`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: leadKey(leadId) });
      void qc.invalidateQueries({ queryKey: leadPromptsKey(leadId) });
      void qc.invalidateQueries({ queryKey: leadMessagesKey(leadId) });
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}
